import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { mensajeDe } from '@/api/errors';
import { useStepUp } from '@/auth/StepUp';
import { useCan } from '@/auth/queries';
import { Campo } from '@/components/Campo';
import { ConfirmarAccion } from '@/components/ConfirmarAccion';
import { Cargando, ErrorCarga } from '@/components/Estados';
import { Modal } from '@/components/Modal';
import { fechaLarga } from '@/lib/formato';
import { cn } from '@/lib/utils';
import { reconocerVideo, urlDeVideo } from '@/lib/video';

import { ElegirLicencias } from '../ElegirLicencias';
import { ArchivoUnico, type ValorArchivo } from '../comun/ArchivoUnico';
import { COMUNES_VACIOS, comunesDe, cuerpoComunes, type Comunes } from '../comun/comunes';
import { DatosComunes } from '../comun/DatosComunes';
import { CasosLigados, LicenciasElemento, NotasContenido } from '../comun/Paneles';
import { useResumenBiblioteca } from '../queries';
import { SIMPLES, type ItemSimple, type TipoSimple } from '../tipos';
import { useBorrarSimple, useGuardarSimple, useItemSimple } from './queries';

interface Propios {
  videoUrl: string;
  appUrl: string;
  longDescription: string;
  archivo: ValorArchivo | null;
  isPlus: boolean;
  isEvaluatedEnarm: boolean;
  folder: string;
}

function propiosDe(d: ItemSimple | null): Propios {
  const x = (d ?? {}) as Partial<Record<string, unknown>>;
  const video = (x.video ?? null) as { provider: string | null; videoId: string } | null;
  return {
    videoUrl: urlDeVideo(video),
    appUrl: (x.appUrl as string | null) ?? '',
    longDescription: (x.longDescription as string | null) ?? '',
    archivo: x.filePath
      ? { path: x.filePath as string, url: (x.fileUrl as string | null) ?? null }
      : null,
    isPlus: Boolean(x.isPlus),
    isEvaluatedEnarm: Boolean(x.isEvaluatedEnarm),
    folder: (x.folder as string | null) ?? '',
  };
}

function Casilla({
  etiqueta,
  valor,
  onCambio,
}: {
  etiqueta: string;
  valor: boolean;
  onCambio: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        className="h-4 w-4 accent-[var(--color-azul-600)]"
        checked={valor}
        onChange={(e) => onCambio(e.target.checked)}
      />
      {etiqueta}
    </label>
  );
}

const PESTANAS = [
  ['datos', 'Datos'],
  ['licencias', 'Licencias'],
  ['notas', 'Notas'],
  ['casos', 'Casos ligados'],
] as const;
type Pestana = (typeof PESTANAS)[number][0];

function Editor({
  tipo,
  actual,
  onListo,
}: {
  tipo: TipoSimple;
  actual: ItemSimple | null;
  onListo: () => void;
}) {
  const editable = useCan('contenido', 'editar');
  const [pestana, setPestana] = useState<Pestana>('datos');
  const [comunes, setComunes] = useState<Comunes>(() =>
    actual ? comunesDe(actual) : COMUNES_VACIOS,
  );
  const [p, setP] = useState<Propios>(() => propiosDe(actual));
  const poner = (c: Partial<Propios>) => setP((v) => ({ ...v, ...c }));
  const [licencias, setLicencias] = useState<Set<number>>(new Set());
  const [borrando, setBorrando] = useState(false);
  const resumen = useResumenBiblioteca();
  const guardar = useGuardarSimple(tipo.clave, actual?.id ?? null);
  const borrar = useBorrarSimple(tipo.clave);
  const { proteger, dialogo } = useStepUp();
  const esVideo = tipo.clave === 'videos' || tipo.clave === 'live-recordings';
  const video = esVideo ? reconocerVideo(p.videoUrl) : null;

  const enviar = () => {
    if (esVideo && !video) {
      toast.error('Escribe una URL de YouTube o Vimeo válida.');
      return;
    }
    if (tipo.archivo && !p.archivo) {
      toast.error('Sube el archivo.');
      return;
    }
    const body: Record<string, unknown> = {
      ...cuerpoComunes(comunes, {
        especialidad: !tipo.sinEspecialidad,
        idioma: !tipo.sinEspecialidad,
      }),
    };
    if (esVideo) {
      body.videoUrl = p.videoUrl.trim();
      body.appUrl = p.appUrl.trim() || null;
    }
    if (tipo.clave === 'live-recordings') body.longDescription = p.longDescription.trim();
    if (tipo.archivo && p.archivo) body.filePath = p.archivo.path;
    if (tipo.clave === 'flashcards' || tipo.clave === 'gpc-guides') body.isPlus = p.isPlus;
    if (tipo.clave === 'flashcards') body.isEvaluatedEnarm = p.isEvaluatedEnarm;
    if (tipo.clave === 'pdfs') body.folder = p.folder.trim() || null;
    if (!actual) body.membershipIds = [...licencias];
    guardar.mutate(body, {
      onSuccess: () => {
        toast.success(actual ? 'Guardado.' : `${tipo.titulo} creado(a).`);
        onListo();
      },
      onError: (e) => toast.error(mensajeDe(e)),
    });
  };

  const pestanas = PESTANAS.filter(
    ([k]) => actual && (k === 'datos' || k === 'licencias' || tipo.notas),
  );

  return (
    <div className="space-y-5">
      {actual && (
        <p className="text-sm text-texto-3">
          {tipo.titulo} · #{actual.id} · biblioteca #{actual.libraryItemId}
          {actual.code && ` · ${actual.code}`}
          {actual.updatedAt && ` · actualizado ${fechaLarga(actual.updatedAt)}`}
        </p>
      )}
      {pestanas.length > 1 && (
        <div role="tablist" aria-label="Secciones" className="flex flex-wrap gap-2">
          {pestanas.map(([k, texto]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={pestana === k}
              className={cn('btn-cs', pestana === k ? 'btn-primario' : 'btn-vidrio')}
              onClick={() => setPestana(k)}
            >
              {texto}
            </button>
          ))}
        </div>
      )}

      {pestana === 'datos' && (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            enviar();
          }}
        >
          <DatosComunes
            valor={comunes}
            onCambio={(c) => setComunes((v) => ({ ...v, ...c }))}
            nombreMax={tipo.nombreMax}
            campos={{ especialidad: !tipo.sinEspecialidad, idioma: !tipo.sinEspecialidad }}
            deshabilitado={!editable}
          />
          <fieldset disabled={!editable} className="space-y-4">
            {esVideo && (
              <>
                <Campo
                  etiqueta="URL del video *"
                  ayuda="YouTube o Vimeo. Se guarda solo el id del video."
                  error={p.videoUrl && !video ? 'No parece una URL de YouTube o Vimeo.' : undefined}
                >
                  <input
                    className="input-vidrio"
                    value={p.videoUrl}
                    required
                    onChange={(e) => poner({ videoUrl: e.target.value })}
                  />
                </Campo>
                {video && (
                  <div className="mx-auto aspect-video w-full max-w-md overflow-hidden rounded-xl bg-black">
                    <iframe
                      src={video.embedUrl}
                      title="Vista previa"
                      className="h-full w-full"
                      allow="fullscreen; picture-in-picture"
                    />
                  </div>
                )}
                <Campo etiqueta="URL de la app" ayuda="Opcional: enlace para la app móvil.">
                  <input
                    className="input-vidrio"
                    value={p.appUrl}
                    maxLength={tipo.clave === 'videos' ? 250 : 500}
                    onChange={(e) => poner({ appUrl: e.target.value })}
                  />
                </Campo>
              </>
            )}
            {tipo.clave === 'live-recordings' && (
              <Campo etiqueta="Descripción para el alumno *">
                <textarea
                  className="input-vidrio min-h-28"
                  value={p.longDescription}
                  required
                  maxLength={5000}
                  onChange={(e) => poner({ longDescription: e.target.value })}
                />
              </Campo>
            )}
            {tipo.archivo && (
              <ArchivoUnico
                destino={tipo.archivo.destino}
                tipos={tipo.archivo.tipos}
                valor={p.archivo}
                deshabilitado={!editable}
                etiqueta={
                  tipo.archivo.tipos.includes('image') ? 'Imagen o archivo *' : 'Archivo PDF *'
                }
                onCambio={(archivo) => poner({ archivo })}
              />
            )}
            {tipo.clave === 'pdfs' && (
              <Campo etiqueta="Carpeta" ayuda="Agrupa los PDF en la biblioteca.">
                <input
                  className="input-vidrio"
                  value={p.folder}
                  maxLength={100}
                  onChange={(e) => poner({ folder: e.target.value })}
                />
              </Campo>
            )}
            {(tipo.clave === 'flashcards' || tipo.clave === 'gpc-guides') && (
              <div className="flex flex-wrap gap-4">
                <Casilla etiqueta="Plus" valor={p.isPlus} onCambio={(v) => poner({ isPlus: v })} />
                {tipo.clave === 'flashcards' && (
                  <Casilla
                    etiqueta="Evaluada en el ENARM"
                    valor={p.isEvaluatedEnarm}
                    onCambio={(v) => poner({ isEvaluatedEnarm: v })}
                  />
                )}
              </div>
            )}
          </fieldset>
          {!actual && resumen.data && (
            <section>
              <h3 className="mb-2 font-extrabold">Licencias</h3>
              <ElegirLicencias
                licencias={resumen.data.memberships}
                elegidas={licencias}
                onCambio={(id, si) =>
                  setLicencias((s) => {
                    const n = new Set(s);
                    if (si) n.add(id);
                    else n.delete(id);
                    return n;
                  })
                }
              />
            </section>
          )}
          {editable && (
            <div className="flex flex-wrap justify-end gap-2">
              {actual && (
                <button
                  type="button"
                  className="btn-cs btn-vidrio mr-auto text-rojo-texto"
                  onClick={() => setBorrando(true)}
                >
                  <Trash2 size={16} aria-hidden /> Borrar
                </button>
              )}
              <button type="button" className="btn-cs btn-contorno" onClick={onListo}>
                Cancelar
              </button>
              <button type="submit" className="btn-cs btn-primario" disabled={guardar.isPending}>
                {guardar.isPending ? 'Guardando…' : actual ? 'Guardar' : 'Crear'}
              </button>
            </div>
          )}
        </form>
      )}
      {actual && pestana === 'licencias' && (
        <LicenciasElemento libraryItemId={actual.libraryItemId} asignadas={actual.membershipIds} />
      )}
      {actual && tipo.notas && pestana === 'notas' && (
        <NotasContenido destino={{ contentType: tipo.notas, contentId: actual.id }} />
      )}
      {actual && tipo.notas && pestana === 'casos' && (
        <CasosLigados destino={{ contentType: tipo.notas, contentId: actual.id }} />
      )}
      {actual && (
        <ConfirmarAccion
          abierto={borrando}
          onCerrar={() => setBorrando(false)}
          titulo={`Borrar «${actual.name}»`}
          descripcion="Se quita de la biblioteca y de todas las licencias. Si algún alumno ya lo vio, no se puede borrar: quítalo de las licencias para ocultarlo. El archivo del CDN no se borra."
          textoBoton="Borrar"
          peligro
          ocupado={borrar.isPending}
          onConfirmar={() =>
            void proteger(() => borrar.mutateAsync(actual.id))
              .then(() => {
                toast.success('Borrado.');
                setBorrando(false);
                onListo();
              })
              .catch((e: unknown) => toast.error(mensajeDe(e)))
          }
        />
      )}
      {dialogo}
    </div>
  );
}

/** Alta (`id` null) o edición de un video, grabación, flashcard, guía GPC o PDF. */
export function ModalSimple({
  tipo,
  id,
  abierto,
  onCerrar,
}: {
  tipo: string | null;
  id: number | null;
  abierto: boolean;
  onCerrar: () => void;
}) {
  const t = tipo ? SIMPLES[tipo] : undefined;
  const q = useItemSimple(t?.clave ?? 'videos', abierto && t ? id : null);
  return (
    <Modal
      abierto={abierto && t !== undefined}
      onCerrar={onCerrar}
      titulo={id === null ? `Nuevo: ${t?.titulo ?? ''}` : (q.data?.name ?? t?.titulo ?? '')}
      ancho="max-w-2xl"
    >
      {t && id === null && <Editor tipo={t} actual={null} onListo={onCerrar} />}
      {t && id !== null && (
        <>
          {q.isPending && <Cargando />}
          {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
          {q.data && <Editor key={q.data.id} tipo={t} actual={q.data} onListo={onCerrar} />}
        </>
      )}
    </Modal>
  );
}
